import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, AlertCircle, Star, Phone } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { useTranslation } from '../i18n';
const WEB3FORMS_ACCESS_KEY = 'a7f2e5e4-a0ef-4a6e-a281-2d4c3076403e';
const LEAVE_REVIEW_URL = 'https://g.page/r/CUmhstMLWUqlEBM/review';
export function CallbackForm() {
  const {
    t,
    lang
  } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    privacyConsent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameLabel = lang === 'en' ? 'Your name' : lang === 'ua' ? "Ваше ім'я" : 'Ваше имя';
  const phoneLabel = lang === 'en' ? 'Phone number' : lang === 'ua' ? 'Номер телефону' : 'Номер телефона';
  const submitLabel = lang === 'en' ? 'Request a call back' : lang === 'ua' ? 'Замовити дзвінок' : 'Заказать звонок';
  const formTitle = lang === 'en' ? 'Request a Call Back' : lang === 'ua' ? 'Замовити зворотний дзвінок' : 'Заказать обратный звонок';
  const formSubtitle = lang === 'en' ? 'Leave your name and phone — we will call you back shortly.' : lang === 'ua' ? "Залиште ім'я та телефон — ми зателефонуємо вам найближчим часом." : 'Оставьте имя и телефон — мы перезвоним вам в ближайшее время.';
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('callbackForm.required');
    if (!formData.phone.trim()) newErrors.phone = t('callbackForm.required');
    if (!formData.privacyConsent) newErrors.privacyConsent = t('callbackForm.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: 'New Callback Request — Dentico Clinic',
          from_name: 'Dentico Clinic Website',
          botcheck: '',
          name: formData.name,
          phone: formData.phone,
          required_consent: 'Yes',
          page_url: window.location.href,
          submitted_at: new Date().toLocaleString()
        })
      });
      const result = await response.json();
      if (result.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          phone: '',
          privacyConsent: false
        });
      } else {
        setError(t('callbackForm.errorMessage'));
      }
    } catch (err) {
      setError(t('callbackForm.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <Card className="p-8 text-center bg-teal-50 border-teal-100" data-id="element-70">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-71">
          <Check className="w-8 h-8 text-teal-600" data-id="element-72" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-73">
          {t('callbackForm.successTitle')}
        </h3>
        <p className="text-gray-600 mb-8" data-id="element-74">{t('callbackForm.successMessage')}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-75">
          <Button onClick={() => setIsSuccess(false)} variant="outline" data-id="element-76">
            {lang === 'en' ? 'Send another request' : lang === 'ua' ? 'Надіслати ще один запит' : 'Отправить ещё один запрос'}
          </Button>
          <a href={LEAVE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-id="element-77">
            <Button variant="primary" className="gap-2" data-id="element-78">
              <Star className="w-4 h-4" data-id="element-79" />
              {t('reviews.leaveReview')}
            </Button>
          </a>
        </div>
      </Card>;
  }
  return <Card className="p-6 sm:p-8" data-id="element-80">
      <div className="mb-6" data-id="element-81">
        <div className="flex items-center gap-3 mb-3" data-id="element-82">
          <div className="p-2 bg-teal-100 rounded-lg" data-id="element-83">
            <Phone className="w-5 h-5 text-teal-600" data-id="element-84" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-85">{formTitle}</h2>
        </div>
        <p className="text-gray-600" data-id="element-86">{formSubtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-87">
        <input type="checkbox" name="botcheck" className="hidden" style={{
        display: 'none'
      }} data-id="element-88" />

        {/* Name */}
        <div data-id="element-89">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-90">
            {nameLabel} <span className="text-red-500" data-id="element-91">*</span>
          </label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder={lang === 'en' ? 'John' : lang === 'ua' ? 'Олена' : 'Александр'} className={`w-full rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500 transition-colors text-lg py-3 ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`} data-id="element-92" />
          {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-93">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div data-id="element-94">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-95">
            {phoneLabel} <span className="text-red-500" data-id="element-96">*</span>
          </label>
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+44..." className={`w-full rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500 transition-colors text-lg py-3 ${errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`} data-id="element-97" />
          {errors.phone && <p className="mt-1 text-sm text-red-600" data-id="element-98">{errors.phone}</p>}
        </div>

        {/* Privacy Consent */}
        <div className="flex items-start gap-3 pt-2" data-id="element-99">
          <div className="flex items-center h-5" data-id="element-100">
            <input id="privacyConsent" name="privacyConsent" type="checkbox" checked={formData.privacyConsent} onChange={handleChange} className={`h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 ${errors.privacyConsent ? 'border-red-300' : ''}`} data-id="element-101" />
          </div>
          <div className="text-sm" data-id="element-102">
            <label htmlFor="privacyConsent" className="text-gray-600" data-id="element-103">
              {t('callbackForm.privacyConsent')}{' '}
              <span className="text-red-500" data-id="element-104">*</span>
            </label>
            {errors.privacyConsent && <p className="mt-1 text-sm text-red-600" data-id="element-105">
                {errors.privacyConsent}
              </p>}
          </div>
        </div>

        {/* Submit */}
        <Button type="submit" fullWidth size="lg" isLoading={isSubmitting} className="mt-4" data-id="element-106">
          {submitLabel}
        </Button>

        {error && <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg text-sm" data-id="element-107">
            <AlertCircle className="w-4 h-4 shrink-0" data-id="element-108" />
            <p data-id="element-109">{error}</p>
          </div>}

        <p className="text-xs text-gray-500 text-center mt-3" data-id="element-110">
          {t('callbackForm.legalText').replace('Privacy Policy', '___PRIVACY___').replace('Patient Privacy Notice', '___PATIENT___').replace('Політикою конфіденційності', '___PRIVACY___').replace('Повідомленням про конфіденційність пацієнта', '___PATIENT___').replace('Политикой конфиденциальности', '___PRIVACY___').replace('Уведомлением о конфиденциальности пациента', '___PATIENT___').split(/(___PRIVACY___|___PATIENT___)/).map((part, i) => {
          if (part === '___PRIVACY___') {
            return <Link key={i} to={`/${lang}/privacy-policy`} className="text-teal-600 hover:underline" data-id="element-111">
                    {lang === 'en' ? 'Privacy Policy' : lang === 'ua' ? 'Політикою конфіденційності' : 'Политикой конфиденциальности'}
                  </Link>;
          }
          if (part === '___PATIENT___') {
            return <Link key={i} to={`/${lang}/patient-privacy-notice`} className="text-teal-600 hover:underline" data-id="element-112">
                    {lang === 'en' ? 'Patient Privacy Notice' : lang === 'ua' ? 'Повідомленням про конфіденційність пацієнта' : 'Уведомлением о конфиденциальности пациента'}
                  </Link>;
          }
          return part;
        })}
        </p>
      </form>
    </Card>;
}